import { apiClient } from './client';

/**
 * 기준 설정 — 회사가 정하는 «값» (2026-08-28 · 자리 재정의 판 7).
 *
 * 서버: `apps/chat-server/src/modules/company-codes/company-codes.routes.ts`.
 *
 * ★★ **읽기는 전 직원에게 열려 있다.** 이 값들은 신청 화면의 선택지라, 직원이 못 읽으면
 *    휴가 종류를 고를 수 없다. **쓰기만** 담당자(그 메뉴를 받은 사람)의 것이다 — 403.
 * ★★ **표가 비면 지금과 똑같다.** 바닥은 서버 코드가 가진 기본 묶음이고, 표는 그 위에 얹는다.
 * ★ **옛 서버는 이 라우트를 모른다**(404) — 부르는 화면이 그 사실을 말하고 접힌다.
 */

/**
 * ★ `approval` 은 2026-08-29 에 는다(판 10 · 계정과목). 읽기는 전 직원, **쓰기는 회사 관리자** —
 *   결재 메뉴는 전 직원이 받아서 「받았는가」로 물으면 전원이 통과한다(서버가 그렇게 가른다).
 */
export type CodeDomain = 'attendance' | 'hr' | 'approval';

export interface CompanyCodeDto {
  code: string;
  label: string;
  active: boolean;
  /** 코드가 원래 갖고 있던 값인가 — **지울 수 없고 끄기만 된다**(지난 기록이 가리킨다). */
  builtin: boolean;
  /**
   * **물러난 값**인가 (S6 · 2026-09-16) — 지난 기록의 이름을 풀어 주려고 남아 있을 뿐,
   * 고르는 목록(`GET /company-codes/:setKey`)에서는 서버가 이미 걸러 낸다.
   * ★ 그래서 화면은 「씀」을 **켤 수 없게** 막는다 — 켜도 선택지에 안 나와 「켰는데 안 나온다」가 된다.
   * ★ **옛 서버는 이 칸을 안 준다**(undefined) — 그때는 종전 그대로 평범한 값이다.
   */
  retired?: boolean;
}

export interface CompanyCodeSetDto {
  key: string;
  label: string;
  domain: CodeDomain;
  /** 왜 여는가 — 화면이 그대로 보여 준다. */
  hint: string;
  /**
   * ★★ **값을 «보탤» 수 있는가 — 지금은 일곱 다 아니다.**
   *    일곱 다 서버 스키마의 닫힌 enum 이라, 보탠 값은 화면에서 고를 수는 있어도
   *    **신청이 400** 이 된다. 그래서 이 판이 여는 것은 **이름과 씀/안 씀** 둘이다.
   *    (서버 `code-sets.ts` 의 `addable` 주석에 여는 조건이 적혀 있다.)
   */
  addable?: boolean;
  codes: CompanyCodeDto[];
}

export interface CompanyCodeItemInput {
  code: string;
  label?: string | null;
  active?: boolean;
}

/** 한 도메인의 목록을 **한 번에** 받는다 — 목록마다 왕복하면 일곱 번 간다. */
export async function fetchCompanyCodeSets(domain?: CodeDomain): Promise<CompanyCodeSetDto[]> {
  const { data } = await apiClient.get<{ sets: CompanyCodeSetDto[] }>('/company-codes', {
    params: domain ? { domain } : undefined,
  });
  return data.sets;
}

/** 한 목록 — **고르는 쪽**(신청 화면)이 부르는 자리. */
export async function fetchCompanyCodes(setKey: string): Promise<CompanyCodeSetDto> {
  const { data } = await apiClient.get<CompanyCodeSetDto>(`/company-codes/${setKey}`);
  return data;
}

/**
 * 그 목록을 **통째로** 저장한다.
 *
 * ★ 기본 값을 빼고 보내도 지워지지 않는다 — 서버가 «끔»(`active:false`)으로 남긴다.
 */
export async function saveCompanyCodes(
  setKey: string,
  items: CompanyCodeItemInput[],
): Promise<CompanyCodeDto[]> {
  const { data } = await apiClient.put<{ key: string; codes: CompanyCodeDto[] }>(
    `/company-codes/${setKey}`,
    { items },
  );
  return data.codes;
}
